import { getPhysicalIOLabel } from '../Utils/physical_io_label';
import React from 'react';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import { multiIOMapping } from '../Constants/constants';
import { getMultiIOOverwriteLabel } from '../Utils/info_utils';
import { supportsMultiIoOverrides } from '../Utils/multi_io_settings';
import { MULTI_IO_SHORT_LABEL } from '../components/MultiIOView';
import {
  getMultiIOCurrentLabel,
  getMultiIOFunctionIndexLabel,
  getMultiIOHardwareTypeLabel,
  getMultiIOStateValue
} from './MultiOutLabel';

const useStyles = makeStyles((theme) => ({
  functionName: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere'
  },
  state: {
    color: theme.palette.primary.main
  },
  unavailableState: {
    color: theme.palette.text.secondary
  },
  current: {
    color: theme.palette.primary.main
  }
}));

export default function MultiIOInfoTableRow({ controller, port, slaveAddress = port.slave }) {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const type = controller.zones ? (port.type_id ?? '') : (port.type ?? '');
  const functionName = t(multiIOMapping[port.func] ?? multiIOMapping['default']);
  const functionIndex = getMultiIOFunctionIndexLabel(controller, port.func, port.func_index, t, port.zone);
  const state = getMultiIOStateValue(controller, port.func, port.state ?? '', type, t).trim();
  const current = getMultiIOCurrentLabel(port.info ?? '', port.info_type ?? port.type ?? '', t, port.monitor);
  const hardwareType = getMultiIOHardwareTypeLabel(controller, type, t);
  const stateUnavailable = !state || state.includes('---');
  const overwriteLabel = supportsMultiIoOverrides(controller)
    ? getMultiIOOverwriteLabel(port.overwr_select, port.overwr_val_ana, type, t) : null;
  const primaryTextStyle = { fontSize: theme.typography.body1.fontSize };
  const secondaryTextStyle = { fontSize: theme.typography.body2.fontSize };

  return (
    <TableRow>
      <TableCell data-testid="multi-io-table-function">
        <Typography className={classes.functionName} style={primaryTextStyle}>
          {functionName}
          {functionIndex !== null ? ` [${functionIndex}]` : ''}
        </Typography>
      </TableCell>
      <TableCell data-testid="multi-io-table-status">
        <Typography
          className={`${classes.state} ${stateUnavailable ? classes.unavailableState : ''}`}
          style={primaryTextStyle}
        >
          {state || '---'}
        </Typography>
        {overwriteLabel && (
          <Typography variant="body2" color="primary" data-testid="multi-io-overwrite-status">
            {overwriteLabel}
          </Typography>
        )}
        {current && (
          <Typography className={classes.current} style={secondaryTextStyle}>
            {current}
          </Typography>
        )}
      </TableCell>
      <TableCell data-testid="multi-io-table-memo">
        <Typography variant="body2" style={secondaryTextStyle}>
          {port.memo || '\u2014'}
        </Typography>
      </TableCell>
      <TableCell data-testid="multi-io-table-port">
        <Typography variant="body2" style={secondaryTextStyle}>
          {MULTI_IO_SHORT_LABEL} {getPhysicalIOLabel(controller, slaveAddress, port.id)}
        </Typography>
      </TableCell>
      <TableCell data-testid="multi-io-table-type">
        <Typography variant="body2" style={secondaryTextStyle}>
          {hardwareType}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
